import { useNavigate, useSearch } from '@tanstack/react-router'
import { resolvePostLoginPath, type RedirectUser } from 'shared/post-login'

// Post-auth redirect, as a hook so `navigate`/`location`/`redirect` live HERE instead of being injected —
// the navigate option types are validated right at the navigate calls below (no inverted responsibility).
// Returns a function taking the resolved user (the login mutation result, or the refetched getUser) and
// sending them to their post-login destination. Destination logic is shared with the server OAuth callback
// (shared/post-login) so the two never drift.
//
// `resolvePostLoginPath` returns navigate options: `{ to }` is a LITERAL route (type-checked against the
// route tree below), `{ href }` is a fully-built URL — navigate's escape hatch handles it (relative →
// client nav showing the destination's pending skeleton; absolute → hard nav). No window.location;
// token/lang/clientId/ref are kept across client navs by the root `retainSearchParams` middleware.
//
// Lives in its own module (like the old lib/handle-redirect) so `resolvePostLoginPath`'s transitive zod
// form schemas load only with the auth/register routes that use this, not the main bundle.
export function usePostLoginRedirect() {
  const navigate = useNavigate()
  const { redirect } = useSearch({ strict: false })

  return (user: RedirectUser) => {
    const dest = resolvePostLoginPath(user, { redirect })
    if ('href' in dest) navigate({ href: dest.href, replace: true })
    else navigate({ to: dest.to, replace: true })
  }
}
